import React from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { Layout, Menu, Dropdown, Avatar, Space, Modal, Alert as AntAlert } from 'antd';
import {
  DashboardOutlined,
  PlusCircleOutlined,
  DatabaseOutlined,
  EditOutlined,
  LogoutOutlined,
  UserOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import Dashboard from '../Dashboard/Dashboard';
import ProductOnboarding from '../ProductOnboarding/ProductOnboarding';
import UpdateWizard from '../UpdateWizard';
import ViewData from '../ViewData';
import DataRelationships from '../DataRelationships/DataRelationships';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import useAuth from '../../auth/useAuth';
import useIdleTimeout from '../../hooks/useIdleTimeout';
import { logger } from '../../utils/logger';
import { useFieldConfig } from '../../contexts/FieldConfigContext';
import { FieldConfigProvider } from '../../contexts/FieldConfigContext';
import { RoleSwitcher } from '../../auth/rbac';
import { IDLE_TIMEOUT_MS, IDLE_WARNING_MS } from '../../config/appConfig';

const { Header, Content, Footer } = Layout;

const AuthenticatedLayoutInner: React.FC = () => {
  const { authState, oktaAuth } = useAuth();
  const navigate = useNavigate();
  const [isWarningModalVisible, setIsWarningModalVisible] = React.useState(false);
  const { isNavDisabled, navigationConfig } = useFieldConfig();

  // Clear the auth_in_progress flag once we successfully render authenticated layout
  React.useEffect(() => {
    sessionStorage.removeItem('auth_in_progress');
  }, []);

  const handleLogout = async () => {
    try {
      logger.info('User logging out');
      sessionStorage.removeItem('scc_active_role');
      sessionStorage.removeItem('scc_role_selected');
      await oktaAuth.signOut();
      navigate('/login');
    } catch (err) {
      logger.error('Logout error:', err);
    }
  };

  const handleIdleTimeout = async () => {
    setIsWarningModalVisible(false);
    logger.warn('Session expired due to inactivity');
    Modal.info({
      title: 'Session Expired',
      content: 'Your session has expired due to inactivity. Please log in again.',
      onOk: handleLogout,
    });
  };

  const handleIdleWarning = () => {
    logger.warn('Showing idle timeout warning');
    setIsWarningModalVisible(true);
  };

  const handleStayActive = () => {
    logger.info('User chose to stay active');
    setIsWarningModalVisible(false);
  };

  useIdleTimeout({
    timeoutMs: IDLE_TIMEOUT_MS,
    onTimeout: handleIdleTimeout,
    warningMs: IDLE_WARNING_MS,
    onWarning: handleIdleWarning,
  });

  const userEmail = authState?.idToken?.claims?.email as string || 'User';
  const userName = authState?.idToken?.claims?.name as string || userEmail;

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: userName,
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout className="layout" style={{ minHeight: '100vh' }}>
      <Modal
        title="Session Expiring Soon"
        open={isWarningModalVisible}
        onOk={handleStayActive}
        onCancel={handleLogout}
        okText="Stay Active"
        cancelText="Log Out"
      >
        <p>Your session will expire in 1 minute due to inactivity.</p>
        <p>Click &quot;Stay Active&quot; to continue your session or &quot;Log Out&quot; to sign out now.</p>
      </Modal>
      <Header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        width: '100%',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <div className="logo" style={{ color: 'white', fontSize: '18px', fontWeight: 'bold', marginRight: '40px' }}>
            Product Onboarding System
          </div>
          <Menu theme="dark" mode="horizontal" defaultSelectedKeys={['1']} style={{ flex: 1, minWidth: 0 }}>
            <Menu.Item key="1" icon={<DashboardOutlined />}>
              <Link to="/">Dashboard</Link>
            </Menu.Item>
            {!isNavDisabled('onboard') && (
              <Menu.Item key="2" icon={<PlusCircleOutlined />}>
                <Link to="/onboard">Onboard Product/SKU</Link>
              </Menu.Item>
            )}
            {!isNavDisabled('update') && (
              <Menu.Item key="3" icon={<EditOutlined />}>
                <Link to="/update">Update Entities</Link>
              </Menu.Item>
            )}
            <Menu.Item key="4" icon={<DatabaseOutlined />}>
              <Link to="/view">View Data</Link>
            </Menu.Item>
            <Menu.Item key="5" icon={<ShareAltOutlined />}>
              <Link to="/relationships">Data Relationships</Link>
            </Menu.Item>
          </Menu>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <RoleSwitcher />
          <ThemeToggle />
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space style={{ cursor: 'pointer', color: 'white' }}>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              <span>{userEmail.split('@')[0]}</span>
            </Space>
          </Dropdown>
        </div>
      </Header>
      <Content style={{ padding: '0 50px', marginTop: 80 }}>
        <div className="site-layout-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/onboard" element={
              isNavDisabled('onboard')
                ? <AntAlert message="Access Denied" description={navigationConfig['onboard']?.reason || 'You do not have permission to access this page.'} type="error" showIcon style={{ marginTop: 24 }} />
                : <ProductOnboarding />
            } />
            <Route path="/update" element={
              isNavDisabled('update')
                ? <AntAlert message="Access Denied" description={navigationConfig['update']?.reason || 'You do not have permission to access this page.'} type="error" showIcon style={{ marginTop: 24 }} />
                : <UpdateWizard />
            } />
            <Route path="/view" element={<ViewData />} />
            <Route path="/relationships" element={<DataRelationships />} />
          </Routes>
        </div>
      </Content>
      <Footer style={{ textAlign: 'center' }}>
        Product Onboarding System ©{new Date().getFullYear()} Created for CSP
      </Footer>
    </Layout>
  );
};

/**
 * Wraps the layout in FieldConfigProvider so all child components
 * can access the memoised, role-based field config via useFieldConfig().
 */
const AuthenticatedLayout: React.FC = () => (
  <FieldConfigProvider>
    <AuthenticatedLayoutInner />
  </FieldConfigProvider>
);

export default AuthenticatedLayout;
